import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  CheckCircle,
  Printer,
  Copy,
  Calendar,
  User,
  Phone,
  CreditCard,
  MapPin,
  Clock,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable Booking Receipt Component
 * Can be used standalone or inside a modal
 * 
 * @param {Object} props
 * @param {Object} props.payment - Payment/booking data object
 * @param {boolean} props.showHeader - Whether to show success/status header (default: true)
 * @param {boolean} props.showActions - Whether to show action buttons (default: true)
 * @param {React.ReactNode} props.customActions - Optional custom action buttons
 * @param {string} props.variant - 'success' | 'confirmed' | 'pending' | 'cancelled' (default: based on payment status)
 */
const BookingReceipt = ({ 
  payment, 
  showHeader = true, 
  showActions = true, 
  customActions,
  variant
}) => {
  // Determine variant based on payment status if not provided
  const displayVariant = variant || (
    payment?.payment_status === 'paid' || payment?.payment_status === 'confirmed' ? 'success' :
    payment?.payment_status === 'pending' ? 'pending' :
    payment?.payment_status === 'cancelled' ? 'cancelled' : 'success'
  );

  const formatTime = (time) => {
    if (!time) return '';
    const timePart = time.includes(':') ? time : `${time}:00`;
    try {
      return new Date(`2000-01-01T${timePart}`).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return time;
    }
  };

  const formatTimeSlots = (slots) => {
    if (!slots || slots.length === 0) return '';
    const sorted = [...slots].sort();
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const lastEndHour = parseInt(last.split(':')[0]) + 1;
    const lastEnd = `${String(lastEndHour).padStart(2, '0')}:00`;
    return `${formatTime(first)} - ${formatTime(lastEnd)}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handlePrint = () => {
    window.print();
  };

  const getStatusConfig = () => {
    switch (displayVariant) {
      case 'success':
      case 'confirmed':
        return {
          icon: CheckCircle,
          iconColor: 'text-green-500',
          bgColor: 'bg-green-500/20',
          accentColor: 'bg-green-500',
          title: 'Payment Successful',
          subtitle: 'Thank you! Your booking is confirmed.',
          badgeClass: 'bg-green-100 text-green-800',
          badgeText: 'Booking Confirmed'
        };
      case 'pending':
        return {
          icon: AlertCircle,
          iconColor: 'text-amber-500',
          bgColor: 'bg-amber-500/20',
          accentColor: 'bg-amber-500',
          title: 'Payment Pending',
          subtitle: 'Your booking is awaiting payment.',
          badgeClass: 'bg-amber-100 text-amber-800',
          badgeText: 'Pending Payment'
        };
      case 'cancelled':
        return {
          icon: XCircle,
          iconColor: 'text-red-500',
          bgColor: 'bg-red-500/20',
          accentColor: 'bg-red-500',
          title: 'Booking Cancelled',
          subtitle: 'This booking has been cancelled.',
          badgeClass: 'bg-red-100 text-red-800',
          badgeText: 'Cancelled'
        };
      default:
        return {
          icon: CheckCircle,
          iconColor: 'text-green-500',
          bgColor: 'bg-green-500/20',
          accentColor: 'bg-green-500',
          title: 'Booking Receipt',
          subtitle: 'Your booking details.',
          badgeClass: 'bg-green-100 text-green-800',
          badgeText: 'Confirmed'
        };
    }
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  if (!payment) return null;

  return (
    <div className="w-full print:w-full print:max-w-none">
      {/* Print-specific styles */}
      <style>{`
        @media print {
          @page {
            size: 80mm auto;
            margin: 5mm;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print-receipt {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border: none !important;
          }
          .print-receipt * {
            box-shadow: none !important;
          }
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          .print-compact {
            padding: 8px !important;
            margin: 4px 0 !important;
          }
          .print-text-sm {
            font-size: 11px !important;
          }
          .print-text-base {
            font-size: 12px !important;
          }
          .print-text-lg {
            font-size: 14px !important;
          }
          .print-text-xl {
            font-size: 18px !important;
          }
          .print-border-dark {
            border-color: #333 !important;
          }
        }
      `}</style>

      {/* Success Header - Hidden when printing */}
      {showHeader && (
        <div className="text-center mb-6 no-print">
          <div className={`inline-flex items-center justify-center w-16 h-16 ${statusConfig.bgColor} rounded-full mb-3`}>
            <StatusIcon className={`h-10 w-10 ${statusConfig.iconColor}`} />
          </div>
          <h1 className="text-2xl font-bold text-black">{statusConfig.title}</h1>
          <p className="text-slate-400">{statusConfig.subtitle}</p>
        </div>
      )}

      {/* Receipt Card */}
      <div className="print-receipt relative">
        <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-1 ${statusConfig.accentColor} rounded-b-full shadow-lg no-print`}></div>

        <Card className="bg-white text-slate-900 border shadow-xl overflow-hidden rounded-2xl print:shadow-none print:border print:border-slate-300 print:rounded-lg">
          <CardContent className="p-0">
            {/* Print Header - Only visible when printing */}
            <div className="hidden print-only text-center py-4 border-b border-dashed border-slate-300">
              <h2 className="text-lg font-bold">CamSur Pickleball Club</h2>
              <p className="text-xs text-slate-500">Official Booking Receipt</p>
            </div>

            {/* Amount */}
            <div className="bg-slate-50 p-6 text-center border-b border-dashed border-slate-200 print:p-4 print:bg-white">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">
                {displayVariant === 'pending' ? 'Amount Due' : 'Total Paid'}
              </p>
              <h2 className="text-4xl font-black text-slate-900 print:text-2xl print-text-xl">
                ₱{payment.total_amount}
              </h2>
            </div>

            {/* Details */}
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 print:p-4 print:gap-3 print:grid-cols-2">
              <section className="space-y-3 print:space-y-1">
                <h3 className="text-xs uppercase font-bold text-slate-400 flex items-center gap-2">
                  <User size={14} /> Customer
                </h3>
                <div>
                  <p className="font-bold text-base print:text-sm print-text-base">{payment.name}</p>
                  {payment.contact_number && (
                    <p className="text-slate-500 text-sm flex items-center gap-1 print-text-sm">
                      <Phone size={12} /> {payment.contact_number}
                    </p>
                  )}
                  {payment.email && (
                    <p className="text-slate-500 text-xs print-text-sm">{payment.email}</p>
                  )}
                </div>
              </section>

              <section className="space-y-3 print:space-y-1">
                <h3 className="text-xs uppercase font-bold text-slate-400 flex items-center gap-2">
                  <CreditCard size={14} /> Payment
                </h3>
                <div>
                  <p className="capitalize font-medium text-sm print-text-sm">
                    {payment.payment_type?.replace(/_/g, ' ')} Payment
                  </p>
                  <p className="text-slate-500 text-xs print-text-sm">
                    {new Date(payment.created_at).toLocaleDateString('en-US', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })} •{' '}
                    {new Date(payment.created_at).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                  <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 bg-slate-200 rounded-full text-xs font-mono text-slate-600 print:bg-slate-100 print:border print:border-slate-300">
                    {payment.transaction_code}
                    <button 
                      onClick={() => copyToClipboard(payment.transaction_code)}
                      className="hover:text-blue-600 transition-colors no-print"
                    >
                      <Copy size={10} />
                    </button>
                  </div>
                </div>
              </section>
            </div>

            {/* Venue Info */}
            {payment.venue_name && (
              <div className="mx-6 mb-3 flex items-center gap-2 text-slate-600 text-sm print:mx-4 print-text-sm">
                <MapPin size={14} />
                <span className="font-medium">{payment.venue_name}</span>
              </div>
            )}

            {/* Booking Info */}
            <div className="mx-6 p-4 bg-slate-900 rounded-xl text-white mb-4 print:mx-4 print:p-3 print:bg-slate-100 print:text-slate-900 print:border print:border-slate-300 print:rounded-lg">
              <h3 className="text-xs uppercase font-bold text-slate-500 mb-3 flex items-center gap-2 print:text-slate-600">
                <Calendar size={12} /> Booking Schedule
              </h3>

              {payment.booking_infos?.map((booking, index) => (
                <div
                  key={booking.id || index}
                  className="flex justify-between items-center"
                >
                  <div>
                    <p className="text-base font-semibold print:text-sm print-text-base">
                      {booking.court_name || `Court ${booking.court_no}`}
                    </p>
                    <p className="text-slate-400 text-xs print:text-slate-600 print-text-sm">
                      {new Date(booking.booking_date).toLocaleDateString(
                        'en-US',
                        {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        }
                      )}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="text-green-400 font-mono text-base print:text-green-700 print:text-sm print-text-base">
                      {booking.time_slots?.length > 0 
                        ? formatTimeSlots(booking.time_slots)
                        : formatTime(booking.booking_time_start)
                      }
                    </p>
                    <p className="text-slate-500 text-xs flex items-center justify-end gap-1 print:text-slate-600 print-text-sm">
                      <Clock size={10} />
                      {booking.duration_hours || 1} hour{(booking.duration_hours || 1) > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Add-ons / Equipment */}
            {(payment.add_ons?.length > 0 || payment.equipment_rentals?.length > 0) && (
              <div className="mx-6 mb-4 p-3 bg-slate-100 rounded-lg print:mx-4 print:border print:border-slate-300">
                <h3 className="text-xs uppercase font-bold text-slate-500 mb-2">
                  Add-ons & Equipment
                </h3>
                <div className="space-y-1">
                  {[...(payment.add_ons || []), ...(payment.equipment_rentals || [])].map((item, idx) => (
                    <div key={idx} className="flex justify-between text-xs print-text-sm">
                      <span>{item.name} x{item.quantity || 1}</span>
                      <span className="font-medium">₱{(item.price * (item.quantity || 1)).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Status Badge */}
            <div className="mx-6 mb-6 print:mx-4 print:mb-4">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 ${statusConfig.badgeClass} rounded-full text-sm font-medium`}>
                <StatusIcon size={14} />
                {statusConfig.badgeText}
              </div>
            </div>

            {/* Print Footer - Only visible when printing */}
            <div className="hidden print-only border-t border-dashed border-slate-300 p-4 text-center">
              <p className="text-xs text-slate-500 mb-1">Thank you for booking with us!</p>
              <p className="text-xs text-slate-400">Please present this receipt upon arrival.</p>
              <p className="text-xs text-slate-400 mt-2">Printed: {new Date().toLocaleString()}</p>
            </div>

            {/* Actions */}
            {showActions && (
              <div className="p-6 pt-0 flex flex-col sm:flex-row gap-2 no-print">
                {customActions || (
                  <Button 
                    onClick={handlePrint}
                    className="flex-1 bg-slate-900 hover:bg-slate-800 text-white py-5 rounded-xl font-bold active:scale-95"
                  >
                    <Printer className="mr-2 h-4 w-4" />
                    Print Receipt
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BookingReceipt;
